import { useState } from "react";
import { useReferral } from "@/hooks/useReferral";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Copy, Share2, Gift, Check } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ReferralShareDialog } from "./ReferralShareDialog";

interface Props {
  userId: string | undefined;
  compact?: boolean;
  className?: string;
}

export const ReferralChip = ({ userId, compact, className }: Props) => {
  const { t } = useTranslation("admin");
  const { data, isLoading } = useReferral(userId);
  const [copied, setCopied] = useState<"code" | "url" | "reward" | null>(null);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  if (isLoading || !data) return null;

  const copy = async (text: string, which: "code" | "url" | "reward") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      const msg = which === "url"
        ? t("referral.copied_link", "Referral link copied")
        : t("referral.copied", "Copied!");
      toast.success(msg);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const openShareDialog = () => setShareDialogOpen(true);

  const pendingReward = data.rewards[0];

  if (compact) {
    return (
      <Button
        variant="outline"
        className="gap-2 border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary font-semibold w-full justify-between"
        onClick={() => {
          copy(data.shareUrl, "url");
          toast.success(t("referral.copied_link", "Referral link copied"));
        }}
        aria-label={t("referral.copy_link", "Copy referral link")}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Gift className="h-4 w-4 shrink-0" />
          <span className="truncate">{t("referral.invite_friends", "Invite a friend, earn rewards")}</span>
        </div>
        <span className="text-[10px] font-bold text-primary-foreground bg-primary rounded-full px-2 py-0.5 shrink-0">
          {data.current_cycle}/10
        </span>
      </Button>
    );
  }

  return (
    <div className={`flex flex-col gap-2 w-full ${className ?? ""}`}>
      {/* Your code card — newsletter-style gradient with blur blobs */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-primary shadow-sm flex-1 flex items-center">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-6 -right-6 w-24 h-24 bg-secondary rounded-full blur-2xl" />
          <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-accent rounded-full blur-2xl" />
        </div>

        <div className="relative z-10 px-3 py-1.5 flex items-center justify-between gap-2">
          <div className="min-w-0 flex flex-col leading-tight">
            <div className="flex items-center gap-1.5 text-primary-foreground/90">
              <Gift className="h-3 w-3 shrink-0" />
              <span className="text-[10px] uppercase tracking-wider font-semibold">
                {t("referral.title", "Your code")}
              </span>
              <span className="text-[10px] font-bold text-primary-foreground bg-white/20 backdrop-blur-sm rounded-full px-1.5 py-0">
                {data.current_cycle}/10
              </span>
            </div>
            <p className="text-[9px] text-primary-foreground/75 leading-tight truncate">
              {t("referral.how_to_use", "Refer 10 people and get a discount coupon.")}
            </p>
            <code className="text-sm font-mono font-bold text-primary-foreground tracking-wide truncate">
              {data.code}
            </code>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
                    onClick={() => copy(data.shareUrl, "url")}
                    aria-label={t("referral.copy_link", "Copy referral link")}
                  >
                    {copied === "url" ? <Check className="h-3.5 w-3.5 text-emerald-300" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("referral.copy_link", "Copy referral link")}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
                    onClick={openShareDialog}
                    aria-label={t("referral.share", "Share link")}
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("referral.share", "Share link")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {pendingReward && (
        <button
          onClick={() => copy(pendingReward.code, "reward")}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-2 py-1 text-[11px] font-medium text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-950/50 transition-colors self-start"
          aria-label="Copy reward code"
        >
          <Gift className="h-3 w-3" />
          <span className="font-semibold">{t("referral.reward_ready", "50% reward:")}</span>
          <code className="font-mono">{pendingReward.code}</code>
          {copied === "reward" ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
        </button>
      )}

      <ReferralShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        shareUrl={data.shareUrl}
        referralCode={data.code}
      />
    </div>
  );
};
